import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle2, ArrowRight, User } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useDropdown } from '../contexts/DropdownContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  'Profilini oluştur, portfolyonu sergile',
  'Standart ve etkinlik sektöründe iş bul',
  'Contractor, Exhibitor ve Supplier firmalarla çalış',
  'Kategori bazlı görünürlük sağla',
  'Güvenli ödeme ve değerlendirme sistemi',
  'Tek platform, tüm iş akışların',
];

export default function FreelancerSignup() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { countries } = useDropdown();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', confirmPassword: '',
    category_id: '', city: '', country: '', base_currency: 'USD', accept_terms: false,
  });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/freelancer-categories`)
      .then(r => setCategories(r.data?.categories || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const fillTestData = () => {
    // İlk yüklü kategoriyi kullan, yoksa sonradan set edilecek
    const firstCatId = categories.length > 0 ? categories[0].id : '';
    setForm({
      first_name: 'David',
      last_name: 'Richter',
      email: 'd.richter@standdesign-pro.com',
      password: '123456Ab&',
      confirmPassword: '123456Ab&',
      category_id: firstCatId,
      city: 'Hamburg',
      country: 'DE',
      base_currency: 'EUR',
      accept_terms: true,
    });
    setErrors({});
    setServerError('');
    // Eğer kategoriler henüz yüklenmediyse, yüklenince ilkini seç
    if (!firstCatId && categories.length === 0) {
      const timer = setTimeout(() => {
        setForm(prev => ({ ...prev, category_id: categories[0]?.id || prev.category_id }));
      }, 1500);
      return () => clearTimeout(timer);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Ad gerekli';
    if (!form.last_name.trim()) e.last_name = 'Soyad gerekli';
    if (!form.email.trim()) e.email = 'E-posta gerekli';
    if (!form.password) e.password = 'Şifre gerekli';
    else if (form.password.length < 8) e.password = 'En az 8 karakter';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Şifreler eşleşmiyor';
    if (!form.category_id) e.category_id = 'Kategori seçiniz';
    if (!form.accept_terms) e.accept_terms = 'Şartları kabul etmeniz gerekiyor';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true); setServerError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/freelancer-signup`, {
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, password: form.password,
        category_id: form.category_id, city: form.city,
        country: form.country, base_currency: form.base_currency,
      });
      const result = await login(form.email, form.password);
      if (!result.success) {
        setServerError(result.error || 'Giriş başarısız');
        return;
      }
      navigate('/app/freelancer/dashboard');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(typeof detail === 'object' ? detail?.message : detail || 'Kayıt başarısız');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Left — benefits */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-red-900 via-red-800 to-rose-900 flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 30% 40%, #fff 1px, transparent 1px), radial-gradient(circle at 70% 60%, #fff 1px, transparent 1px)', backgroundSize:'40px 40px'}} />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2 text-white font-black text-xl tracking-tight">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-red-700 font-black text-sm">ES</span>
            </div>
            EXPO STAND
          </Link>
        </div>
        <div className="relative space-y-8">
          <div>
            <span className="text-red-300 text-xs font-bold uppercase tracking-widest">Freelancer Portalı</span>
            <h1 className="text-4xl font-black text-white mt-2 leading-tight">
              Yeteneklerini<br />Dünyaya Göster
            </h1>
            <p className="text-red-200 mt-4 text-sm leading-relaxed">
              Stand ve etkinlik sektöründe 28+ kategoride uzmanlarla buluşan tek platform.
            </p>
          </div>
          <div className="space-y-3">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-red-700 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-3 h-3 text-red-200" />
                </div>
                <span className="text-red-100 text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <p className="text-red-300 text-xs">Zaten hesabın var mı?{' '}
            <Link to="/freelancer/login" className="text-white font-semibold hover:underline">Giriş yap →</Link>
          </p>
        </div>
      </div>

      {/* Right — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="w-12 h-12 rounded-2xl bg-red-600 flex items-center justify-center mb-4">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-black text-gray-900">Freelancer Hesabı Oluştur</h2>
                <p className="text-gray-500 text-sm mt-1">Ücretsiz profil oluştur, iş almaya başla</p>
              </div>
              <button type="button" onClick={fillTestData}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-red-400/50 text-red-500 text-xs font-semibold hover:bg-red-50 transition-colors"
                data-testid="fill-test-data-btn">
                ⚡ Fill Test Data
              </button>
            </div>
          </div>

          {serverError && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-xl text-red-300 text-sm">
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input value={form.first_name} onChange={e => set('first_name', e.target.value)}
                  placeholder="Ad" className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-red-500 transition-colors" />
                {errors.first_name && <p className="text-red-400 text-xs mt-1">{errors.first_name}</p>}
              </div>
              <div>
                <input value={form.last_name} onChange={e => set('last_name', e.target.value)}
                  placeholder="Soyad" className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-red-500 transition-colors" />
                {errors.last_name && <p className="text-red-400 text-xs mt-1">{errors.last_name}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="E-posta adresi" className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-red-500 transition-colors" />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Category */}
            <div>
              <select value={form.category_id} onChange={e => set('category_id', e.target.value)}
                className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-red-500 transition-colors">
                <option value="">Kategori seç *</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {errors.category_id && <p className="text-red-400 text-xs mt-1">{errors.category_id}</p>}
            </div>

            {/* Country + City */}
            <div className="grid grid-cols-2 gap-3">
              <select value={form.country} onChange={e => set('country', e.target.value)}
                className="bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-red-500 transition-colors">
                <option value="">Ülke</option>
                {countries.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
              </select>
              <input value={form.city} onChange={e => set('city', e.target.value)}
                placeholder="Şehir" className="bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-red-500 transition-colors placeholder-gray-400" />
            </div>

            {/* Currency */}
            <select value={form.base_currency} onChange={e => set('base_currency', e.target.value)}
              className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-red-500 transition-colors">
              {['USD','EUR','GBP','TRY','AED'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            {/* Password */}
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)}
                placeholder="Şifre (en az 8 karakter)" className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 pr-10 focus:outline-none focus:border-red-500 transition-colors" />
              <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>
            <div>
              <input type="password" value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)}
                placeholder="Şifreyi tekrarla" className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-red-500 transition-colors" />
              {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>

            {/* Terms */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.accept_terms} onChange={e => set('accept_terms', e.target.checked)} className="mt-1 accent-red-600" />
              <span className="text-gray-500 text-xs">
                <Link to="/terms" className="text-red-400 hover:underline">Kullanım Şartları</Link>nı ve{' '}
                <Link to="/privacy" className="text-red-400 hover:underline">Gizlilik Politikası</Link>nı kabul ediyorum.
              </span>
            </label>
            {errors.accept_terms && <p className="text-red-400 text-xs">{errors.accept_terms}</p>}

            <button type="submit" disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
              {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <>Hesap Oluştur <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="text-center text-gray-500 text-xs mt-6">
            Zaten hesabın var mı?{' '}
            <Link to="/freelancer/login" className="text-red-400 hover:text-red-300">Giriş yap</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
